import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Play,
  ArrowLeft,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/toastContext';
import { streamJobEvents, type SseEvent } from '../../api/sse';

interface CollabMode {
  key: string;
  label: string;
  description: string;
  min_members: number;
  roles: string[];
  default_prompts: Record<string, string>;
}

interface Agent {
  id: string;
  name: string;
  description?: string;
  status: string;
}

interface Room {
  id: string;
  name: string;
  mode: string;
  status: 'draft' | 'running' | 'completed' | 'failed';
  agent_ids: string[];
  member_roles: Record<string, string>;
  member_prompts: Record<string, string>;
  moderator_prompt: string | null;
  task: string;
  max_rounds: number;
  outcome: 'pass' | 'max_rounds' | 'completed' | 'error' | null;
  job_id: string | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

interface RoomMessage {
  id: string;
  room_id: string;
  round_no: number;
  from_agent_id: string | null;
  from_agent_name: string | null;
  role: 'implementer' | 'reviewer' | 'participant' | 'moderator' | 'system';
  content: string;
  marker_result: 'PASS' | 'FAIL' | null;
  marker_notes: string | null;
  is_error: boolean;
  created_at: string;
}

const ROLE_LABELS: Record<string, string> = {
  implementer: '实现者',
  reviewer: '评审者',
  participant: '成员',
  moderator: '主持人',
  system: '系统',
};

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  running: '运行中',
  completed: '已完成',
  failed: '失败',
};

const STATUS_BADGE: Record<string, string> = {
  draft: 'secondary',
  running: 'info',
  completed: 'success',
  failed: 'error',
};

const OUTCOME_LABELS: Record<string, string> = {
  pass: '评审通过',
  max_rounds: '达到轮次上限',
  completed: '流程完毕',
  error: '异常终止',
};

const OUTCOME_BADGE: Record<string, string> = {
  pass: 'success',
  max_rounds: 'warning',
  completed: 'info',
  error: 'error',
};

/** 成员发言着色调色板：按 agent 在房间中的 index 取色 */
const MEMBER_COLORS = ['#58a6ff', '#7ee787', '#f0883e', '#d2a8ff', '#ffa198', '#56d4dd', '#e3b341', '#79c0ff'];

/** 按选中顺序推导成员角色：review 模式首位=实现者、次位=评审者；其余模式均为参与者 */
function roleFor(mode: CollabMode | undefined, index: number): string {
  if (!mode) return 'participant';
  if (mode.key === 'review') {
    return mode.roles[Math.min(index, mode.roles.length - 1)] ?? 'participant';
  }
  return mode.roles.includes('participant') ? 'participant' : (mode.roles[0] ?? 'participant');
}

export default function CollabRoom() {
  const navigate = useNavigate();
  const toast = useToast();

  const [modes, setModes] = useState<CollabMode[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [messages, setMessages] = useState<RoomMessage[]>([]);
  const [creating, setCreating] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());

  // 新建表单状态
  const [formName, setFormName] = useState('');
  const [formMode, setFormMode] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [task, setTask] = useState('');
  const [maxRounds, setMaxRounds] = useState(3);
  const [memberPrompts, setMemberPrompts] = useState<Record<string, string>>({});
  const [moderatorPrompt, setModeratorPrompt] = useState('');

  const abortRef = useRef<AbortController | null>(null);
  const chatEnd = useRef<HTMLDivElement>(null);

  const currentMode = modes.find((m) => m.key === formMode);

  const modeLabel = (key: string) => modes.find((m) => m.key === key)?.label || key;
  const agentName = (id: string) => agents.find((a) => a.id === id)?.name || id;
  const memberColor = (room: Room, agentId: string | null) => {
    if (!agentId) return 'var(--text-muted)';
    const idx = room.agent_ids.indexOf(agentId);
    return MEMBER_COLORS[(idx >= 0 ? idx : 0) % MEMBER_COLORS.length];
  };

  const loadRooms = async () => {
    try {
      const res = await apiClient.get('/collab/rooms');
      setRooms(res.data);
    } catch (e) {
      console.error('Failed to load collab rooms:', e);
    }
  };

  const loadRoom = async (roomId: string): Promise<Room | null> => {
    try {
      const res = await apiClient.get(`/collab/rooms/${roomId}`);
      setSelectedRoom(res.data);
      return res.data;
    } catch (e) {
      console.error('Failed to load collab room:', e);
      return null;
    }
  };

  const loadMessages = async (roomId: string) => {
    try {
      const res = await apiClient.get(`/collab/rooms/${roomId}/messages`);
      setMessages(res.data);
    } catch (e) {
      console.error('Failed to load collab messages:', e);
    }
  };

  // 初始加载：模式元数据 / Agent 列表 / 房间列表
  useEffect(() => {
    const tick = async () => {
      try {
        const res = await apiClient.get('/collab/modes');
        setModes(res.data.modes || []);
        if (res.data.modes?.length) setFormMode((prev) => prev || res.data.modes[0].key);
      } catch (e) {
        console.error('Failed to load collab modes:', e);
      }
      try {
        const res = await apiClient.get('/agents');
        setAgents(res.data);
      } catch (e) {
        console.error('Failed to load agents:', e);
      }
      await loadRooms();
    };
    void tick();
  }, []);

  // 组件卸载时中断事件订阅
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleJobEvent = (event: SseEvent, roomId: string) => {
    if (event.type === 'turn' || event.type === 'marker') {
      void loadMessages(roomId);
    }
    if (event.type === 'done') {
      void loadRoom(roomId);
      void loadMessages(roomId);
      void loadRooms();
    }
  };

  // 订阅协作任务事件流：turn/marker 刷新消息，done 刷新房间状态
  const subscribeJob = (jobId: string, roomId: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    streamJobEvents(jobId, (event) => handleJobEvent(event, roomId), controller.signal).catch((e) => {
      if (e?.name === 'AbortError') return;
      console.error('订阅协作事件失败:', e);
    });
  };

  const selectRoom = async (roomId: string) => {
    setCreating(false);
    setExpandedNotes(new Set());
    const room = await loadRoom(roomId);
    await loadMessages(roomId);
    // 房间仍在运行中且有 job_id：自动接上事件流
    if (room?.status === 'running' && room.job_id) {
      subscribeJob(room.job_id, roomId);
    }
  };

  const applyMode = (modeKey: string) => {
    setFormMode(modeKey);
    const mode = modes.find((m) => m.key === modeKey);
    if (!mode) return;
    // 模式切换后按新角色重填默认 prompt（覆盖未提交的旧默认值）
    const next: Record<string, string> = {};
    selectedIds.forEach((id, i) => {
      next[id] = mode.default_prompts[roleFor(mode, i)] || '';
    });
    setMemberPrompts(next);
    setModeratorPrompt(mode.default_prompts['moderator'] || '');
  };

  const toggleMember = (agentId: string) => {
    if (selectedIds.includes(agentId)) {
      setSelectedIds(selectedIds.filter((id) => id !== agentId));
      return;
    }
    const next = [...selectedIds, agentId];
    setSelectedIds(next);
    const mode = modes.find((m) => m.key === formMode);
    if (mode) {
      const role = roleFor(mode, next.indexOf(agentId));
      setMemberPrompts((prev) => ({ ...prev, [agentId]: mode.default_prompts[role] || '' }));
    }
  };

  const resetForm = () => {
    setFormName('');
    setSelectedIds([]);
    setTask('');
    setMaxRounds(3);
    setMemberPrompts({});
    setModeratorPrompt('');
  };

  const createRoom = async () => {
    if (!currentMode || submitting) return;
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        name: formName.trim(),
        mode: currentMode.key,
        agent_ids: selectedIds,
        task: task.trim(),
        max_rounds: maxRounds,
        member_roles: Object.fromEntries(selectedIds.map((id, i) => [id, roleFor(currentMode, i)])),
        member_prompts: Object.fromEntries(selectedIds.map((id) => [id, memberPrompts[id] || ''])),
      };
      if (currentMode.key !== 'review' && moderatorPrompt.trim()) {
        body.moderator_prompt = moderatorPrompt.trim();
      }
      const res = await apiClient.post('/collab/rooms', body);
      toast.success('协作房间已创建');
      setCreating(false);
      resetForm();
      await loadRooms();
      await selectRoom(res.data.id);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      toast.error('创建房间失败' + (typeof detail === 'string' ? `: ${detail}` : ''));
    } finally {
      setSubmitting(false);
    }
  };

  const startRoom = async () => {
    if (!selectedRoom || starting) return;
    setStarting(true);
    try {
      const res = await apiClient.post(`/collab/rooms/${selectedRoom.id}/start`);
      toast.success('协作已启动');
      const room = await loadRoom(selectedRoom.id);
      void loadRooms();
      subscribeJob(res.data.job_id, selectedRoom.id);
      void room;
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { detail?: unknown } } };
      const detail = err.response?.data?.detail;
      toast.error('启动失败' + (typeof detail === 'string' ? `: ${detail}` : ''));
    } finally {
      setStarting(false);
    }
  };

  const toggleNotes = (msgId: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  const canSubmit =
    !!formName.trim() &&
    !!currentMode &&
    selectedIds.length >= (currentMode?.min_members ?? 2) &&
    !!task.trim();

  const renderMessage = (msg: RoomMessage, room: Room) => {
    if (msg.role === 'system') {
      return (
        <div key={msg.id} className="text-muted" style={{ textAlign: 'center', fontSize: 13, margin: '12px 0' }}>
          {msg.content}
        </div>
      );
    }
    const color = memberColor(room, msg.from_agent_id);
    const notesOpen = expandedNotes.has(msg.id);
    return (
      <div
        key={msg.id}
        style={{
          borderLeft: `3px solid ${msg.is_error ? 'var(--color-error)' : color}`,
          background: msg.is_error ? 'rgba(248, 81, 73, 0.12)' : `${color}20`,
          borderRadius: 'var(--radius-md)',
          padding: '10px 12px',
          marginBottom: 10,
        }}
      >
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          <strong style={{ color: msg.is_error ? 'var(--color-error)' : color }}>
            {msg.from_agent_name || '未知成员'}
          </strong>
          <span className="badge secondary">{ROLE_LABELS[msg.role] || msg.role}</span>
          {msg.marker_result && (
            <span className={`badge ${msg.marker_result === 'PASS' ? 'success' : 'error'}`}>{msg.marker_result}</span>
          )}
          {msg.is_error && (
            <span className="badge error">
              <AlertTriangle size={12} style={{ verticalAlign: 'middle' }} /> 异常
            </span>
          )}
        </div>
        <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{msg.content}</div>
        {msg.marker_result === 'FAIL' && msg.marker_notes && (
          <div style={{ marginTop: 6 }}>
            <button
              className="secondary"
              style={{ padding: '2px 8px', fontSize: 12 }}
              onClick={() => toggleNotes(msg.id)}
            >
              {notesOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />} 评审意见
            </button>
            {notesOpen && (
              <div
                className="text-muted"
                style={{ marginTop: 6, fontSize: 13, whiteSpace: 'pre-wrap', paddingLeft: 8 }}
              >
                {msg.marker_notes}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMessages = (room: Room) => {
    let lastRound = -1;
    return messages.map((msg) => {
      const divider = msg.round_no !== lastRound ? (
        <div
          key={`round-${msg.id}`}
          className="text-muted"
          style={{ textAlign: 'center', fontSize: 13, margin: '14px 0 10px', fontWeight: 600 }}
        >
          —— {`第 ${msg.round_no} 轮`} ——
        </div>
      ) : null;
      lastRound = msg.round_no;
      return (
        <div key={msg.id}>
          {divider}
          {renderMessage(msg, room)}
        </div>
      );
    });
  };

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <button className="secondary" onClick={() => navigate('/teaching')}>
          <ArrowLeft size={16} /> 返回示教中心
        </button>
        <h2 style={{ margin: 0 }}>
          <Users size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          多 Agent 协作房间
        </h2>
        <button
          className="primary"
          onClick={() => {
            setCreating(true);
            setSelectedRoom(null);
          }}
        >
          <Plus size={14} /> 新建房间
        </button>
      </div>

      <div className="flex gap-4" style={{ alignItems: 'flex-start' }}>
        {/* 左栏：房间列表 */}
        <div className="card" style={{ width: 300, flexShrink: 0 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>房间列表</h3>
          {rooms.length === 0 && <p className="text-muted text-sm">暂无房间，点击右上角「新建房间」</p>}
          <div className="list">
            {rooms.map((room) => (
              <div
                key={room.id}
                className="list-item"
                style={{
                  cursor: 'pointer',
                  border: selectedRoom?.id === room.id ? '1px solid var(--color-primary)' : undefined,
                }}
                onClick={() => selectRoom(room.id)}
              >
                <div className="list-item-content">
                  <strong>{room.name}</strong>
                  <div className="flex items-center gap-2" style={{ marginTop: 4, flexWrap: 'wrap' }}>
                    <span className="badge secondary">{modeLabel(room.mode)}</span>
                    <span className={`badge ${STATUS_BADGE[room.status] || 'secondary'}`}>
                      {STATUS_LABELS[room.status] || room.status}
                    </span>
                    {room.outcome && (
                      <span className={`badge ${OUTCOME_BADGE[room.outcome] || 'secondary'}`}>
                        {OUTCOME_LABELS[room.outcome] || room.outcome}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 右栏：新建表单 / 房间详情 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {creating ? (
            <div className="card">
              <h3 style={{ margin: '0 0 16px' }}>新建协作房间</h3>

              <div className="flex flex-col gap-4">
                <div>
                  <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 6 }}>
                    房间名称
                  </label>
                  <input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="房间名称"
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 6 }}>
                    协作模式
                  </label>
                  <div className="grid grid-cols-3" style={{ gap: 10 }}>
                    {modes.map((m) => (
                      <label
                        key={m.key}
                        style={{
                          cursor: 'pointer',
                          padding: 12,
                          borderRadius: 'var(--radius-md)',
                          background: 'var(--bg-primary)',
                          border:
                            formMode === m.key ? '1px solid var(--color-primary)' : '1px solid transparent',
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="collab-mode"
                            aria-label={m.label}
                            checked={formMode === m.key}
                            onChange={() => applyMode(m.key)}
                            style={{ width: 'auto' }}
                          />
                          <strong>{m.label}</strong>
                        </div>
                        <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                          {m.description}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 6 }}>
                    参与成员（{selectedIds.length} 已选
                    {currentMode ? `，至少 ${currentMode.min_members} 人` : ''}）
                  </label>
                  {currentMode?.key === 'review' && (
                    <p className="text-muted" style={{ fontSize: 12, margin: '0 0 6px' }}>
                      按勾选顺序分配角色：首位 = 实现者、次位 = 评审者
                    </p>
                  )}
                  <div style={{ background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)', padding: '6px 12px' }}>
                    {agents.length === 0 && <p className="text-muted text-sm">暂无可用 Agent</p>}
                    {agents.map((a) => (
                      <label
                        key={a.id}
                        className="flex items-center gap-2"
                        style={{ cursor: 'pointer', padding: '6px 0' }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(a.id)}
                          onChange={() => toggleMember(a.id)}
                          style={{ width: 'auto' }}
                        />
                        <span>{a.name}</span>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          {a.description || a.status}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 6 }}>
                    任务 / 主题
                  </label>
                  <textarea
                    value={task}
                    onChange={(e) => setTask(e.target.value)}
                    placeholder="任务 / 主题"
                    rows={3}
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 6 }}>
                    轮次上限
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={maxRounds}
                    onChange={(e) => setMaxRounds(Number(e.target.value) || 1)}
                    style={{ width: 120 }}
                  />
                </div>

                {selectedIds.length > 0 && (
                  <div>
                    <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 6 }}>
                      成员角色 Prompt（取自模式默认值，可编辑）
                    </label>
                    <div className="flex flex-col gap-3">
                      {selectedIds.map((id, i) => (
                        <div key={id}>
                          <div className="text-sm" style={{ marginBottom: 4 }}>
                            <strong>{agentName(id)}</strong>
                            <span className="badge secondary" style={{ marginLeft: 8 }}>
                              {ROLE_LABELS[roleFor(currentMode, i)]}
                            </span>
                          </div>
                          <textarea
                            value={memberPrompts[id] || ''}
                            onChange={(e) =>
                              setMemberPrompts((prev) => ({ ...prev, [id]: e.target.value }))
                            }
                            rows={2}
                            style={{ width: '100%' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {currentMode && currentMode.key !== 'review' && (
                  <div>
                    <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 6 }}>
                      主持人 Prompt（moderator）
                    </label>
                    <textarea
                      value={moderatorPrompt}
                      onChange={(e) => setModeratorPrompt(e.target.value)}
                      rows={2}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}

                <div className="flex gap-2">
                  <button className="primary" onClick={createRoom} disabled={!canSubmit || submitting}>
                    {submitting ? '创建中...' : '创建房间'}
                  </button>
                  <button
                    className="secondary"
                    onClick={() => {
                      setCreating(false);
                      resetForm();
                    }}
                  >
                    取消
                  </button>
                </div>
              </div>
            </div>
          ) : selectedRoom ? (
            <>
              <div className="card mb-4">
                <div className="flex items-center justify-between mb-2" style={{ flexWrap: 'wrap', gap: 8 }}>
                  <h3 style={{ margin: 0 }}>{selectedRoom.name}</h3>
                  <div className="flex gap-2">
                    <button
                      className="secondary"
                      onClick={() => {
                        void loadRoom(selectedRoom.id);
                        void loadMessages(selectedRoom.id);
                        void loadRooms();
                      }}
                    >
                      <RefreshCw size={14} /> 刷新
                    </button>
                    <button
                      className="primary"
                      onClick={startRoom}
                      disabled={selectedRoom.status === 'running' || starting}
                    >
                      <Play size={14} /> {starting ? '启动中...' : '开始协作'}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
                  <span className="badge secondary">{modeLabel(selectedRoom.mode)}</span>
                  <span className={`badge ${STATUS_BADGE[selectedRoom.status] || 'secondary'}`}>
                    {STATUS_LABELS[selectedRoom.status] || selectedRoom.status}
                  </span>
                  {selectedRoom.outcome && (
                    <span className={`badge ${OUTCOME_BADGE[selectedRoom.outcome] || 'secondary'}`}>
                      {OUTCOME_LABELS[selectedRoom.outcome] || selectedRoom.outcome}
                    </span>
                  )}
                  <span className="text-muted text-sm">轮次上限 {selectedRoom.max_rounds}</span>
                </div>

                <p style={{ margin: '0 0 12px', whiteSpace: 'pre-wrap' }}>{selectedRoom.task}</p>

                <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  {selectedRoom.agent_ids.map((id) => {
                    const color = memberColor(selectedRoom, id);
                    return (
                      <span
                        key={id}
                        style={{
                          padding: '4px 10px',
                          borderRadius: 'var(--radius-md)',
                          background: `${color}20`,
                          borderLeft: `3px solid ${color}`,
                          fontSize: 13,
                        }}
                      >
                        <strong style={{ color }}>{agentName(id)}</strong>
                        <span className="text-muted" style={{ marginLeft: 6 }}>
                          {ROLE_LABELS[selectedRoom.member_roles?.[id]] || '成员'}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>

              <div className="card">
                <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>协作消息</h3>
                {messages.length === 0 ? (
                  <div className="empty" style={{ padding: '30px' }}>
                    <p>暂无消息，点击「开始协作」启动多 Agent 协作</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
                    {renderMessages(selectedRoom)}
                    <div ref={chatEnd} />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="card empty" style={{ padding: '60px' }}>
              <Users size={36} className="text-muted" />
              <p>选择左侧房间查看详情，或点击「新建房间」创建多 Agent 协作</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
